<template>
  <el-dialog
    id="create-modal"
    class="form-modal"
    :visible.sync="createVisible"
    v-loading="createProcessing || ttsProcessing"
    :fullscreen="true"
    :show-close="false"
    :modal="false"
  >
    <div id="create-modal-header" class="modal-header">
      <div class="modal-header-layout flex grid-6 align-center">
        <h1 class="modal-title col-2">Create Mediafile</h1>
        <div class="modal-actions col-2">
          <el-button type="primary" @click="createOperation">Create</el-button>
          <el-button type="default" @click="cancelOperation">Cancel</el-button>
        </div>
      </div>
    </div>

    <el-form>
      <el-input type="text" v-model="mediafileName" placeholder="Enter name">
        <template slot="prefix">Name</template>
      </el-input>
      <h2 class="form-section-header">Record or upload</h2>
      <div class="mt-20 mb-10">
        <div class="inline-form-group">
          <audio id="mediafile" style="display: none" src=""></audio>

          <div class="mediafile-controls-flex flex align-center">
            <el-button
              v-loading="mediafilePlayPending"
              class="mediafile-play-button mr-5"
              :disabled="!!!mediafileBlob && !mediafileExists"
              :icon="mediafileIsPlaying ? 'mdi mdi-pause' : 'mdi mdi-play'"
              @click="mediafileIsPlaying ? pauseFile() : playFile()"
              circle
              type="primary"
            >
            </el-button>
            <el-tooltip
              placement="top"
              content="Press and hold to record"
              class="flex align-center justify-center"
            >
              <div>
                <VueRecordAudio
                  ref="vueRecordermediafile"
                  mode="hold"
                  @stream="gotStreamMediafile"
                  @result="onDoneRecordingMediafile"
                />
              </div>
            </el-tooltip>
            <el-tooltip placement="top" content="Upload file">
              <el-upload
                class="ml-5 mr-5"
                action="hailFred"
                accept="audio/*"
                :auto-upload="false"
                :on-change="showUploadedMediafile"
                :show-file-list="false"
              >
                <el-button class="mr-0" circle icon="el-icon-upload2">
                </el-button>
              </el-upload>
            </el-tooltip>

            <div class="mediafile-save-actions ml-30">
              <el-tooltip
                placement="top"
                content="Cancel changes"
                v-if="mediafileChanged"
              >
                <el-button
                  type="danger"
                  circle
                  icon="mdi mdi-cancel"
                  @click="cancelChanges"
                >
                </el-button>
              </el-tooltip>
            </div>
          </div>
        </div>
      </div>

      <text-to-speech
        :clearFields="clearFormFields"
        media-file-text=""
        @processingStart="ttsProcessing = true"
        @processingEnd="ttsProcessing = false"
        @processingComplete="onTTSComplete"
      />
    </el-form>
  </el-dialog>
</template>

<script>
import TextToSpeech from "./textToSpeech.vue";
export default {
  name: "MediafilesCreateForm",

  props: {
    createVisible: { required: true },
    clearFormFields: { required: true },
    createProcessing: { required: true }
  },

  components: {
    TextToSpeech
  },

  data() {
    return {
      mediafileIsPlaying: false,
      mediafileAudioElement: null,
      doneRecording: null,
      mediafileBlob: null,
      mediafileName: null,
      mediafileChanged: false,
      mediafileExists: false,
      mediafileDownloadPending: false,
      mediafilePlayPending: false,
      ttsProcessing: false,
      lastMediaText: "",
    };
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    }
  },

  watch: {
    clearFormFields(value) {
      if (value) {
        this.clearFields();
      }
    }
  },

  methods: {
    onTTSComplete(data) {
      const { blob, text } = data;
      this.mediafileBlob = blob;
      this.lastMediaText = text;
      this.mediafileAudioElement.src = window.URL.createObjectURL(
        this.mediafileBlob
      );
      this.mediafileChanged = true;

      this.$notify({
        type: "success",
        title: "Success",
        message: "The preview is ready for playback."
      });
    },

    normalizeFileName(name) {
      // Normalize to NFD (decomposed form) and remove diacritical marks
      return name.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    },

    sanitizeText(text) {
      if (!text) return text;
      const map = {
        "\u2018": "'", "\u2019": "'", "\u201a": "'", "\u201b": "'",
        "\u201c": '"', "\u201d": '"', "\u201e": '"', "\u201f": '"',
        "\u2032": "'", "\u2033": '"',
        "\u2010": "-", "\u2011": "-", "\u2012": "-", "\u2013": "-",
        "\u2014": "-", "\u2015": "-", "\u2212": "-",
        "\u00a0": " ", "\u2007": " ", "\u2009": " ", "\u202f": " ",
        "\u200b": "", "\u00ad": "",
        "\u2026": "...", "\u2022": "*",
      };
      return text.replace(
        /[\u2018-\u201f\u2032\u2033\u2010-\u2015\u2212\u00a0\u2007\u2009\u202f\u200b\u00ad\u2026\u2022]/g,
        ch => (map[ch] !== undefined ? map[ch] : ch)
      );
    },

    cancelOperation() {
      this.$emit("cancelOperation", "create");
    },

    gotStreamMediafile(data) {
      this.mediafileBlob = null;
      this.doneRecording = null;
    },

    onDoneRecordingMediafile(data) {
      this.doneRecording = true;
      this.mediafileBlob = data;
      this.mediafileAudioElement.src = window.URL.createObjectURL(data);
      this.mediafileChanged = true;
    },

    async playFile() {
      if (!!this.mediafileBlob) {
        this.mediafileAudioElement.play();
        this.mediafileIsPlaying = true;
      }
    },

    pauseFile() {
      this.mediafileIsPlaying = false;
      this.mediafileAudioElement.pause();
      this.mediafileAudioElement.currentTime = 0;
    },

    cancelChanges() {
      this.mediafileBlob = null;
      this.mediafileChanged = false;
      this.doneRecording = false;
    },

    showUploadedMediafile(file) {
      this.mediafileBlob = new Blob([file.raw]);
      this.mediafileAudioElement.src = window.URL.createObjectURL(
        this.mediafileBlob
      );
      this.mediafileChanged = true;
    },

    createOperation() {
      if (!this.mediafileBlob) {
        this.$notify({
          type: "error",
          title: "Empty mediafile",
          message: "You must upload or record one."
        });
        return;
      }

      if (!this.mediafileName) {
        this.$notify({
          type: "error",
          title: "Mediafile name is empty",
          message: "You must input the mediafile name."
        });
        return;
      }

      const mediaFile = this.mediafileBlob;

      let formData = new FormData();
      formData.append("mediaFile", mediaFile);
      formData.append("name", this.normalizeFileName(this.mediafileName));
      formData.append("tenantId", this.selectedTenant.id);
      formData.append("text", this.sanitizeText(this.lastMediaText));
      this.$emit("createOperation", formData);
    },

    clearExistingmediafile() {
      this.mediafileBlob = null;
      this.mediafileName = null;
      this.mediafileExists = false;
      this.mediafileRecord = null;
    },

    setDownloadPending() {
      this.mediafileDownloadPending = !this.mediafileDownloadPending;
    },

    setPlayPending() {
      this.mediafilePlayPending = !this.mediafilePlayPending;
    },

    clearFields() {
      this.mediafileIsPlaying = false;
      this.doneRecording = null;
      this.mediafileBlob = null;
      this.mediafileName = null;
      this.mediafileChanged = false;
      this.mediafileExists = false;
      this.mediafileDownloadPending = false;
      this.mediafilePlayPending = false;
      this.lastMediaText = "";
    }
  },

  mounted() {
    this.mediafileAudioElement = document.querySelector("audio");

    if (this.mediafileAudioElement) {
      this.mediafileAudioElement.addEventListener("ended", () => {
        this.mediafileIsPlaying = false;
      });
    }
  },

  beforeDestroy() {
    if (this.mediafileAudioElement) {
      this.mediafileAudioElement.removeEventListener("ended", () => true);
    }
  }
};
</script>

<style lang="scss" scoped>
.el-form {
  max-width: 600px;
  margin: 0 auto;
}

.vue-audio-recorder {
  width: 40px !important;
  height: 40px !important;
  background-color: #ef5350 !important;
}

.mediafile-play-button {
  width: 55px;
  height: 55px;
  i {
    font-size: 2em;
  }
}

hr {
  opacity: 0.25;
}
</style>
